import { ChangeEvent, FormEventHandler, useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Product, ProductSupplierCostEntry, noImage } from "../product.entity";
import { v4, v5 } from "uuid";
import { productRepository } from "../product.repository";
import { AppError, AppErrorKey } from "../../common/app-error";
import { RepositoryErrorKey } from "../../common/repository.firebase";
import Swal from "sweetalert2";
import { Shell } from "../../common/shell/shell.page";
import {
    Autocomplete,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Grid,
    IconButton,
    Stack,
    Switch,
    TextField,
    Tooltip,
    Typography,
    createFilterOptions,
} from "@mui/material";
import { Add, DeleteOutline, Error, LockOutlined } from "@mui/icons-material";
import ValueObjectObservable from "../../common/vo/value-object-observable";
import { MoneyInput } from "../../common/components/money-input";
import application from "../../core/application";
import { getDownloadURL, uploadBytes, uploadBytesResumable } from "firebase/storage";
import { ProductCategory } from "../product-category.entity";
import { productCategoriesRepository } from "../product-categories.repository";
import { ProductSupplier } from "../product-supplier.entity";
import { LoadingOverlay } from "../../common/components/loading-overlay";
import { PageHeader } from "../../common/components/page-header";
import { productSupplierRepository } from "../product-supplier.repository";
import { Permission } from "../../users/user.entity";
import { ProductImageQRCode } from "../product-image-qrcode.entity";
import { SwalReact } from "../../common/swal-react";
import QRCode from "react-qr-code";
import { ProductImageQRCodeRepository, productImageQRCodeRepository } from "../product-image-qrcode.repository";
import { aiEnhanceProductImage, aiExtractProductFromImage } from "../../ai/callable-ai";
import { isInsufficientAiCreditsError } from "../../ai/ai-credit-guard";
import { getFirebaseStorageObjectPathFromUrl, readFileAsBase64 } from "../../ai/file-utils";
import { suggestProductNames } from "../../ai/product-name-suggestions";
import {
    type CategorySupplierAiSuggestion,
    suggestCategoryAndSupplier,
} from "../../ai/suggest-category-supplier";
import { useModuleAccess } from "../../settings/module-access";
import { ModuleLockedView } from "../../settings/components/module-locked-view";
import { analyticsService } from "../../core/analytics";
import { AI_CREDIT_COSTS, AiCreditCostHint } from "../../ai/ai-credit-guard";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as ChartTooltip, Legend, ResponsiveContainer } from "recharts";
import { recordPriceHistory, getPriceHistory, type PriceHistoryEntry } from "../product-price-history.service";

function PriceHistoryChart({ productId }: { productId: string }) {
    const [history, setHistory] = useState<PriceHistoryEntry[] | null>(null);
    const [user] = application.user.useState();
    const canSeeCost = user?.hasPermission([Permission.READ_WRITE_PRODUCTS_PRIVATE]) ?? false;

    useEffect(() => { getPriceHistory(productId).then(setHistory); }, [productId]);

    if (history === null) return <CircularProgress size={20} sx={{ display: "block", mx: "auto", mt: 2 }} />;
    if (history.length === 0) return null;

    const formatBRL = (v: number) =>
        new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v);
    const fmtDate = (d: Date) => d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });

    const supplierIds = canSeeCost
        ? Array.from(new Set(history.flatMap(h => h.supplierCosts.map(c => c.supplierId))))
        : [];

    const data = history.map(h => {
        const point: Record<string, unknown> = { date: fmtDate(h.changedAt), salePrice: h.salePrice };
        supplierIds.forEach(sid => {
            const cost = h.supplierCosts.find(c => c.supplierId === sid);
            point[`cost_${sid}`] = cost?.purchasePrice;
        });
        return point;
    });

    const COLORS = ["#f59e0b", "#10b981", "#6366f1", "#ec4899"];

    return (
        <Box sx={{ mt: 4 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>Evolução de preços</Typography>
            <ResponsiveContainer width="100%" height={260}>
                <LineChart data={data} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                    <YAxis tickFormatter={formatBRL} tick={{ fontSize: 11 }} width={90} />
                    <ChartTooltip formatter={(v: number, name: string) => [formatBRL(v), name]} />
                    <Legend />
                    <Line type="monotone" dataKey="salePrice" name="Preço de venda" stroke="#2563eb" strokeWidth={2} dot={{ r: 3 }} />
                    {canSeeCost && supplierIds.map((sid, i) => {
                        const name = history.flatMap(h => h.supplierCosts).find(c => c.supplierId === sid)?.supplierName ?? sid;
                        return (
                            <Line key={sid} type="monotone" dataKey={`cost_${sid}`} name={`Custo — ${name}`}
                                stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={{ r: 3 }} connectNulls />
                        );
                    })}
                </LineChart>
            </ResponsiveContainer>
        </Box>
    );
}

/** Rascunho em memória para /produtos/novo (evita id = código e produtos órfãos antes do 1º salvar). */
let symplouNovoProductDraft: Product | null = null;

enum ProductFormMode {
    LOADING,
    ADDING,
    EDITING
}

type ItemAutoComplete = {
    title?: string,
    id: string,
    inputValue?: string
}

class ProductEditableStatefy {
    public readonly attributes = new ValueObjectObservable<{ [key: string]: any }>(this.product.toObject());
    public touched = false;

    constructor(public readonly product: Product) { }

    set(attribute: string, value: any) {
        this.touched = true;
        const setter = `set${attribute.charAt(0).toUpperCase() + attribute.slice(1)}`;

        if (typeof (this.product as any)[setter] === "function") {
            (this.product as any)[setter](value);
            this.attributes.set(this.product.toObject());
            return;
        }

        throw new AppError(AppErrorKey.UNKNOWN_ERROR, `Setter ${setter} não encontrado no produto.`);
    }

    async refreshAttribute(attribute: string) {
        const productInFirebase = await productRepository.findById(this.product.id);
        const getter = `get${attribute.charAt(0).toUpperCase() + attribute.slice(1)}`;

        if (typeof (productInFirebase as any)[getter] === "function") {
            const value = (productInFirebase as any)[getter]();
            this.set(attribute, value);
            return;
        }

        throw new AppError(AppErrorKey.UNKNOWN_ERROR, `Getter ${getter} não encontrado no produto.`);
    }

    async save() {
        if (!this.product) return;

        if (!this.validate()) return;
        await productRepository.save(this.product);

        await Swal.fire({
            title: 'Produto salvo com sucesso!',
            icon: 'success',
            showConfirmButton: false,
            timer: 1500
        });
    }

    validate() {
        if (!this.product) return false;

        if ((this.product.getName() ?? "").trim().length < 2) {
            Swal.fire({
                title: "Nome obrigatório",
                text: "Informe um nome com pelo menos 2 caracteres.",
                icon: "warning",
            });
            return false;
        }

        if (this.product.getTrackStock()) {
            const s = Number(this.product.getStock());
            if (isNaN(s) || s < 0) {
                Swal.fire({
                    title: 'Estoque inválido.',
                    text: 'O estoque precisa ser um número maior ou igual a zero.',
                    icon: 'error'
                });
                return false;
            }
        }

        return true;
    }
}

export const ProductFormPage = () => {
    const { productId } = useParams();

    const [mode, setMode] = useState(ProductFormMode.LOADING);
    const [productEditable, setProductEditable] = useState<ProductEditableStatefy>();
    const [productAttributes, setProductAttributes] = useState<{ [key: string]: any }>({});
    const [imageUploading, setImageUploading] = useState<boolean>(false);
    const [categories, setCategories] = useState<ProductCategory[]>();
    const [suppliers, setSupplier] = useState<ProductSupplier[]>();
    const [savingProduct, setSalvingProduct] = useState(false);
    const [aiFillLoading, setAiFillLoading] = useState(false);
    const [aiEnhanceImageLoading, setAiEnhanceImageLoading] = useState(false);
    /** Pré-visualização pós-IA: só persiste no Storage ao confirmar no modal. */
    const [enhanceImagePreview, setEnhanceImagePreview] = useState<{
        originalUrl: string;
        imageBase64: string;
        mimeType: string;
    } | null>(null);
    const [nameSuggestions, setNameSuggestions] = useState<string[]>([]);
    const [nameSuggestLoading, setNameSuggestLoading] = useState(false);
    const [categorySupplierSuggestion, setCategorySupplierSuggestion] =
        useState<CategorySupplierAiSuggestion | null>(null);
    const [categorySupplierLoading, setCategorySupplierLoading] = useState(false);
    const [categoryModalOpen, setCategoryModalOpen] = useState(false);
    const [supplierModalOpen, setSupplierModalOpen] = useState(false);
    const [newCategoryTitle, setNewCategoryTitle] = useState("");
    const [newCategoryDescription, setNewCategoryDescription] = useState("");
    const [newSupplierTitle, setNewSupplierTitle] = useState("");
    const [addSupplierEntry, setAddSupplierEntry] = useState<ItemAutoComplete | null>(null);
    const [addSupplierPrice, setAddSupplierPrice] = useState<number>(0);
    const moduleAccess = useModuleAccess();
    const [user] = application.user.useState();
    const priceSnapshotRef = useRef<{
        salePrice: number;
        supplierCosts: { supplierId: string; supplierName: string; purchasePrice: number }[];
    } | null>(null);

    const navigate = useNavigate();

    application.useHasPermission(Permission.WRITE_PRODUCTS);

    useEffect(() => {
        productCategoriesRepository.getAll().then(setCategories);
        productSupplierRepository.getAll().then(setSupplier);
    }, []);

    useEffect(() => {
        const name = (productAttributes.name ?? "").trim();
        if (name.length < 3) {
            setNameSuggestions([]);
            return;
        }
        const handle = window.setTimeout(() => {
            setNameSuggestLoading(true);
            suggestProductNames(name)
                .then(setNameSuggestions)
                .catch(() => setNameSuggestions([]))
                .finally(() => setNameSuggestLoading(false));
        }, 700);
        return () => window.clearTimeout(handle);
    }, [productAttributes.name]);

    useEffect(() => {
        const name = (productAttributes.name ?? "").trim();
        if (name.length < 3 || !categories || !suppliers) {
            setCategorySupplierSuggestion(null);
            return;
        }
        const handle = window.setTimeout(() => {
            setCategorySupplierLoading(true);
            suggestCategoryAndSupplier({
                productName: name,
                categories: categories.map((c) => ({ id: c.id, title: c.title })),
                suppliers: suppliers.map((s) => ({ id: s.id, title: s.title })),
            })
                .then(setCategorySupplierSuggestion)
                .catch(() => setCategorySupplierSuggestion(null))
                .finally(() => setCategorySupplierLoading(false));
        }, 900);
        return () => window.clearTimeout(handle);
    }, [productAttributes.name, categories, suppliers]);

    useEffect(() => {
        if (!productEditable) return;
        const eventId = productEditable.attributes.addEventListener((attributes) => {
            setProductAttributes(attributes);
        });
        setProductAttributes(productEditable.attributes.get());

        return () => productEditable.attributes.removeEventListener(eventId);
    }, [productEditable]);


    useEffect(() => {
        if (!productId) {
            if (!symplouNovoProductDraft) {
                symplouNovoProductDraft = new Product(v4(), "", 0, noImage);
            }
            setProductEditable(new ProductEditableStatefy(symplouNovoProductDraft));
            setMode(ProductFormMode.ADDING);
            return;
        }

        symplouNovoProductDraft = null;
        setMode(ProductFormMode.LOADING);
        productRepository.findById(productId).then(product => {
            setProductEditable(new ProductEditableStatefy(product));
            setMode(ProductFormMode.EDITING);
            priceSnapshotRef.current = {
                salePrice: product.getPrice(),
                supplierCosts: product.getSupplierCosts().map(c => ({
                    supplierId: c.supplier.id,
                    supplierName: c.supplier.title ?? c.supplier.id,
                    purchasePrice: c.purchasePrice,
                })),
            };
        }).catch((err) => {
            if (err.key === RepositoryErrorKey.ENTITY_NOT_FOUND) {
                setMode(ProductFormMode.EDITING);
                return;
            }

            throw new AppError(AppErrorKey.UNKNOWN_ERROR, err.message, err.options)
        });
    }, [productId]);

    const changeProductImage = useCallback(async (event: ChangeEvent<HTMLInputElement>) => {
        const fileInput = event.target;
        const file = (fileInput as any)?.files[0];

        if (!file) {
            Swal.fire({
                title: 'Nenhuma imagem selecionada.',
                text: 'Você precisa selecionar uma imagem para continuar.',
                icon: 'error'
            });
            return;
        }

        if (!file.type.startsWith("image/")) {
            Swal.fire({
                title: 'Arquivo inválido.',
                text: 'Você precisa selecionar uma imagem para continuar.',
                icon: 'error'
            });
            return;
        }

        setImageUploading(true);
        const ref = application.getStorageRef(`/products/${productAttributes.id}.${file.type.split("/")[1]}`);
        const uploadTask = uploadBytesResumable(ref, file);

        uploadTask.on(
            'state_changed',
            (snapshot) => {
                const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                console.log(`Upload is ${progress}% done`);
            },
            (error) => {
                console.error('Erro para upar imagem', error);
                Swal.fire({
                    title: 'Erro ao enviar imagem.',
                    text: 'Ocorreu um erro ao enviar a imagem, tente novamente.',
                    icon: 'error'
                })
                setImageUploading(false);
            },
            () => {
                getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
                    productEditable?.set('image', downloadURL);
                    productEditable?.save();
                    setImageUploading(false);
                });
            }
        );
    }, [productAttributes, productEditable]);

    const fillWithAiFromFile = useCallback(
        async (event: ChangeEvent<HTMLInputElement>) => {
            const file = event.target.files?.[0];
            event.target.value = "";
            if (!file || !file.type.startsWith("image/")) {
                await Swal.fire({
                    title: "Imagem inválida",
                    text: "Selecione um arquivo de imagem (JPEG, PNG, etc.).",
                    icon: "error",
                });
                return;
            }
            if (!productEditable || !categories) return;

            setAiFillLoading(true);
            try {
                const base64 = await readFileAsBase64(file);
                const r = await aiExtractProductFromImage({
                    imageBase64: base64,
                    mimeType: file.type,
                    categories: categories.map((c) => ({ id: c.id, name: c.title })),
                });
                if (r.name) {
                    productEditable.set("name", r.name);
                }
                if (r.price != null && !Number.isNaN(Number(r.price))) {
                    productEditable.set("price", Number(r.price));
                }
                if (r.suggestedCategoryId) {
                    const cat = categories.find((c) => c.id === r.suggestedCategoryId);
                    if (cat) {
                        productEditable.set("category", cat);
                    }
                }

                setAiFillLoading(false);
                setImageUploading(true);
                try {
                    const ext = file.type.split("/")[1] || "jpeg";
                    const storageRef = application.getStorageRef(
                        `/products/${productEditable.product.id}.${ext}`
                    );
                    await uploadBytes(storageRef, file);
                    const downloadURL = await getDownloadURL(storageRef);
                    productEditable.set("image", downloadURL);
                    await productRepository.save(productEditable.product);
                } finally {
                    setImageUploading(false);
                }

                await Swal.fire({
                    title: "Sugestões aplicadas",
                    text: "Nome, preço, categoria e a foto foram atualizados. Revise e salve se quiser ajustar mais alguma coisa.",
                    icon: "success",
                    timer: 2500,
                    showConfirmButton: false,
                });
            } catch (raw: unknown) {
                if (isInsufficientAiCreditsError(raw)) return;
                const msg =
                    raw != null &&
                        typeof raw === "object" &&
                        "message" in raw &&
                        typeof (raw as { message?: unknown }).message === "string" ?
                        (raw as { message: string }).message :
                        String(raw);
                await Swal.fire({
                    title: "IA",
                    text: msg,
                    icon: "error",
                });
            } finally {
                setAiFillLoading(false);
                setImageUploading(false);
            }
        },
        [productEditable, categories]
    );

    const enhanceImageWithAi = useCallback(async () => {
        if (!productEditable) return;
        if (noImage === productAttributes.image) {
            await Swal.fire({
                title: "Sem foto",
                text: "Adicione uma foto ao produto antes de melhorar com IA.",
                icon: "warning",
            });
            return;
        }

        setAiEnhanceImageLoading(true);
        try {
            const storageObjectPath = getFirebaseStorageObjectPathFromUrl(productAttributes.image);
            const bk = application.bussiness.get();
            if (!storageObjectPath || !bk) {
                await Swal.fire({
                    title: "Imagem",
                    text: "Só é possível melhorar fotos já salvas no armazenamento deste negócio.",
                    icon: "warning",
                });
                return;
            }
            if (!storageObjectPath.startsWith(`${bk.key}/`)) {
                await Swal.fire({
                    title: "Imagem",
                    text: "Não foi possível validar o arquivo da foto.",
                    icon: "error",
                });
                return;
            }
            const r = await aiEnhanceProductImage({
                storageObjectPath,
                productName: (productAttributes.name ?? "").trim() || undefined,
            });

            setEnhanceImagePreview({
                originalUrl: productAttributes.image,
                imageBase64: r.imageBase64,
                mimeType: r.mimeType,
            });
        } catch (raw: unknown) {
            if (isInsufficientAiCreditsError(raw)) return;
            const msg =
                raw != null &&
                    typeof raw === "object" &&
                    "message" in raw &&
                    typeof (raw as { message?: unknown }).message === "string" ?
                    (raw as { message: string }).message :
                    String(raw);
            await Swal.fire({
                title: "IA — melhorar foto",
                text: msg,
                icon: "error",
            });
        } finally {
            setAiEnhanceImageLoading(false);
        }
    }, [productEditable, productAttributes.image, productAttributes.name]);

    const confirmEnhanceImageSave = useCallback(async () => {
        if (!productEditable || !enhanceImagePreview) return;
        setImageUploading(true);
        try {
            const ext = (enhanceImagePreview.mimeType.split("/")[1] || "png").replace(/[^a-z0-9]/gi, "") || "png";
            const storageRef = application.getStorageRef(
                `/products/${productEditable.product.id}.${ext}`
            );
            const binary = Uint8Array.from(atob(enhanceImagePreview.imageBase64), (c) => c.charCodeAt(0));
            await uploadBytes(storageRef, new Blob([binary], { type: enhanceImagePreview.mimeType }));
            const downloadURL = await getDownloadURL(storageRef);
            productEditable.set("image", downloadURL);
            await productRepository.save(productEditable.product);
            setEnhanceImagePreview(null);
            await Swal.fire({
                title: "Foto atualizada",
                text: "A nova imagem foi salva no produto.",
                icon: "success",
                timer: 2200,
                showConfirmButton: false,
            });
        } catch (raw: unknown) {
            const msg =
                raw != null &&
                    typeof raw === "object" &&
                    "message" in raw &&
                    typeof (raw as { message?: unknown }).message === "string" ?
                    (raw as { message: string }).message :
                    String(raw);
            await Swal.fire({
                title: "Erro ao salvar",
                text: msg,
                icon: "error",
            });
        } finally {
            setImageUploading(false);
        }
    }, [productEditable, enhanceImagePreview]);

    const discardEnhanceImagePreview = useCallback(() => {
        setEnhanceImagePreview(null);
    }, []);

    const applyAiCategoryChip = useCallback(async () => {
        if (!productEditable || !categorySupplierSuggestion) return;
        const { category } = categorySupplierSuggestion;
        if (category.mode === "existing" && category.existingId) {
            const cat = categories?.find((c) => c.id === category.existingId);
            if (cat) {
                productEditable.set("category", cat);
            }
            return;
        }
        if (category.mode === "new" && category.newTitle?.trim()) {
            const title = category.newTitle.trim();
            const r = await Swal.fire({
                title: "Criar nova categoria?",
                html: `Será criada a categoria <strong>${title}</strong> e vinculada a este produto.`,
                icon: "question",
                showCancelButton: true,
                confirmButtonText: "Criar e usar",
                cancelButtonText: "Cancelar",
            });
            if (!r.isConfirmed) return;
            const created = new ProductCategory(v4(), title, "");
            await productCategoriesRepository.save(created);
            setCategories((prev) => (prev ? [...prev, created] : [created]));
            productEditable.set("category", created);
        }
    }, [productEditable, categorySupplierSuggestion, categories]);

    const applyAiSupplierChip = useCallback(async () => {
        if (!productEditable || !categorySupplierSuggestion) return;
        const { supplier } = categorySupplierSuggestion;
        let sup: ProductSupplier | null = null;

        if (supplier.mode === "existing" && supplier.existingId) {
            sup = suppliers?.find((s) => s.id === supplier.existingId) ?? null;
        } else if (supplier.mode === "new" && supplier.newTitle?.trim()) {
            const title = supplier.newTitle.trim();
            const r = await Swal.fire({
                title: "Criar novo fornecedor?",
                html: `Será cadastrado o fornecedor <strong>${title}</strong> e vinculado a este produto.`,
                icon: "question",
                showCancelButton: true,
                confirmButtonText: "Criar e usar",
                cancelButtonText: "Cancelar",
            });
            if (!r.isConfirmed) return;
            sup = new ProductSupplier(v4(), title);
            await productSupplierRepository.save(sup);
            setSupplier((prev) => (prev ? [...prev, sup!] : [sup!]));
        }

        if (!sup) return;

        const currentCosts = (productAttributes.supplierCosts ?? []) as Array<{ supplierId: string; purchasePrice: number }>;
        if (currentCosts.some(c => c.supplierId === sup!.id)) return;
        const resolvedCosts: ProductSupplierCostEntry[] = currentCosts
            .map(c => ({ supplier: suppliers?.find(s => s.id === c.supplierId)!, purchasePrice: c.purchasePrice }))
            .filter(c => c.supplier);
        productEditable.set("supplierCosts", [...resolvedCosts, { supplier: sup, purchasePrice: 0 }]);
    }, [productEditable, categorySupplierSuggestion, suppliers, productAttributes.supplierCosts]);

    const saveNewCategoryFromModal = useCallback(async () => {
        if (!productEditable) return;
        const title = newCategoryTitle.trim();
        if (!title) {
            await Swal.fire({ title: "Informe o nome da categoria.", icon: "warning" });
            return;
        }
        const created = new ProductCategory(v4(), title, newCategoryDescription.trim());
        await productCategoriesRepository.save(created);
        setCategories((prev) => (prev ? [...prev, created] : [created]));
        productEditable.set("category", created);
        setNewCategoryTitle("");
        setNewCategoryDescription("");
        setCategoryModalOpen(false);
    }, [productEditable, newCategoryTitle, newCategoryDescription]);

    const saveNewSupplierFromModal = useCallback(async () => {
        if (!productEditable) return;
        const title = newSupplierTitle.trim();
        if (!title) {
            await Swal.fire({ title: "Informe o nome do fornecedor.", icon: "warning" });
            return;
        }
        const created = new ProductSupplier(v4(), title);
        await productSupplierRepository.save(created);
        setSupplier((prev) => (prev ? [...prev, created] : [created]));
        const currentCosts = (productAttributes.supplierCosts ?? []) as Array<{ supplierId: string; purchasePrice: number }>;
        const resolvedCosts: ProductSupplierCostEntry[] = currentCosts
            .map(c => ({ supplier: suppliers?.find(s => s.id === c.supplierId)!, purchasePrice: c.purchasePrice }))
            .filter(c => c.supplier);
        productEditable.set("supplierCosts", [...resolvedCosts, { supplier: created, purchasePrice: 0 }]);
        setNewSupplierTitle("");
        setSupplierModalOpen(false);
    }, [productEditable, newSupplierTitle, productAttributes.supplierCosts, suppliers]);

    const productImageQRCodeGenerate = useCallback(async () => {
        if (!productEditable) return;

        const productQRCode = new ProductImageQRCode(v4(), productEditable.product.id, v4(), false);
        await productImageQRCodeRepository.save(productQRCode);

        const [productQRCodeVO, unsubscribe] = await productImageQRCodeRepository.findByIdObservable(productQRCode.id);

        const QRCodeModal = SwalReact.mixin({
            title: 'QR Code',
            html: (
                <>
                    <p>Escanei o QRCode com o celular para utilizar o celular para enviar a imagem do produto.</p>
                    <QRCode value={`${window.location.protocol}//${window.location.host}/produtos/qrcode/${productQRCode.id}/${productQRCode.token}}`} />
                </>
            ),
            didClose: () => {
                productImageQRCodeRepository.remove(productQRCode);
                unsubscribe();
            },
            showConfirmButton: false,
            showCancelButton: true,
            cancelButtonText: 'Fechar'
        });

        productQRCodeVO.addEventListener((qrCode) => {
            if (!qrCode) return;
            if (!qrCode.usaged) return;

            productEditable.refreshAttribute('image');
            QRCodeModal.close();
            Swal.fire({
                title: 'Imagem alterada com sucesso!',
                icon: 'success',
                showConfirmButton: false,
                timer: 1500
            });
        });

        QRCodeModal.fire();

    }, [productEditable]);

    if (mode === ProductFormMode.LOADING || moduleAccess.loading) {
        return (
            <Shell title="Produto">
                <LoadingOverlay message="Carregando produto…" subtle />
            </Shell>
        );
    }

    const productsEnabled = moduleAccess.isEnabled("products_sales_basic") ||
        moduleAccess.isEnabled("products_sales_advanced") ||
        moduleAccess.isEnabled("products_sales_unlimited");
    if (!productsEnabled) {
        return (
            <Shell title="Produto">
                <PageHeader title="Produtos" description="Cadastro de produtos." />
                <ModuleLockedView
                    moduleName="Cadastro de produtos e vendas"
                    groupId="products_sales"
                    details="Ative uma variação do módulo para cadastrar e editar produtos."
                    features={["Catálogo de produtos personalizado", "Registro de vendas", "Controle de preços"]}
                />
            </Shell>
        );
    }

    if (mode === ProductFormMode.ADDING && !productEditable) {
        return (
            <Shell title="Novo produto">
                <LoadingOverlay message="Preparando cadastro…" subtle />
            </Shell>
        );
    }

    if (!productEditable) {
        return (
            <Shell title="Produto">
                <Card sx={{ maxWidth: 420, mx: "auto", textAlign: "center", p: 3 }}>
                    <Error color="error" sx={{ fontSize: 48, mb: 2 }} />
                    <Typography variant="h6" gutterBottom>
                        Produto não encontrado
                    </Typography>
                    <Button onClick={() => navigate("/produtos")} variant="contained" fullWidth>
                        Voltar à lista
                    </Button>
                </Card>
            </Shell>
        );
    }

    if (!productAttributes) {
        return (
            <Shell title="Produto">
                <LoadingOverlay message="Preparando formulário…" subtle />
            </Shell>
        );
    }

    return (
        <Shell title={productId ? "Editar produto" : "Novo produto"}>
            <PageHeader
                title={productAttributes.name || "Novo produto"}
                description={
                    productId ?
                        "Ajuste código opcional, nome, preço, estoque, categoria e imagem. Toque em Salvar para gravar." :
                        "Preencha nome e preço. O código de barras ou SKU é opcional."
                }
            />
            <Box sx={{ position: "relative" }}>
                {imageUploading || aiFillLoading || aiEnhanceImageLoading ? (
                    <LoadingOverlay
                        message={
                            aiEnhanceImageLoading ?
                                "Gerando foto profissional com IA…" :
                                aiFillLoading ?
                                    "Analisando imagem com IA…" :
                                    imageUploading && enhanceImagePreview ?
                                        "Salvando nova foto…" :
                                        "Enviando imagem…"
                        }
                        subtle
                    />
                ) : null}
                <input type="file" id="fileImage" name="fileImage" style={{ display: "none" }} title="Adicionar foto" onChange={changeProductImage} />
                <input
                    type="file"
                    id="aiFillProductImage"
                    accept="image/*"
                    style={{ display: "none" }}
                    title="IA — preencher a partir da foto"
                    onChange={fillWithAiFromFile}
                />
                <form>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={5}>
                            <Card elevation={0} sx={{ border: 1, borderColor: "divider", overflow: "hidden" }}>
                                <CardContent sx={{ textAlign: "center" }}>
                                    <Box
                                        component="img"
                                        src={productAttributes.image}
                                        alt=""
                                        sx={{
                                            width: "100%",
                                            maxHeight: 280,
                                            objectFit: "contain",
                                            borderRadius: 1,
                                            bgcolor: "grey.100",
                                        }}
                                    />
                                    <Stack spacing={1} sx={{ mt: 2 }}>
                                        <Button
                                            variant="contained"
                                            onClick={() => document.getElementById("fileImage")?.click()}
                                            disabled={
                                                aiFillLoading ||
                                                imageUploading ||
                                                aiEnhanceImageLoading ||
                                                enhanceImagePreview !== null
                                            }
                                        >
                                            Trocar imagem
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            color="secondary"
                                            onClick={() => document.getElementById("aiFillProductImage")?.click()}
                                            disabled={
                                                aiFillLoading ||
                                                imageUploading ||
                                                aiEnhanceImageLoading ||
                                                enhanceImagePreview !== null
                                            }
                                        >
                                            Preencher com IA (foto)
                                        </Button>
                                        <Box sx={{display: "flex", justifyContent: "center"}}>
                                            <AiCreditCostHint cost={AI_CREDIT_COSTS.extractProductFromImage} />
                                        </Box>
                                        <Button
                                            variant="outlined"
                                            color="secondary"
                                            onClick={() => void enhanceImageWithAi()}
                                            disabled={
                                                aiFillLoading ||
                                                imageUploading ||
                                                aiEnhanceImageLoading ||
                                                enhanceImagePreview !== null ||
                                                noImage === productAttributes.image
                                            }
                                        >
                                            Melhorar foto com IA
                                        </Button>
                                        <Box sx={{display: "flex", justifyContent: "center"}}>
                                            <AiCreditCostHint cost={AI_CREDIT_COSTS.enhanceProductImage} />
                                        </Box>
                                        {("ontouchstart" in window ||
                                            navigator.maxTouchPoints > 0 ||
                                            (navigator as unknown as { msMaxTouchPoints?: number }).msMaxTouchPoints! > 0) ? null : (
                                            <Button variant="outlined" color="warning" onClick={() => productImageQRCodeGenerate()}>
                                                Enviar pelo celular (QR Code)
                                            </Button>
                                        )}
                                        <Button
                                            variant="text"
                                            color="error"
                                            disabled={noImage === productAttributes.image || enhanceImagePreview !== null}
                                            onClick={() => {
                                                productEditable.set("image", noImage);
                                                productEditable.save();
                                            }}
                                        >
                                            Remover imagem
                                        </Button>
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={7}>
                            <Stack spacing={2}>
                                <TextField
                                    id="productCode"
                                    label="Código (SKU ou código de barras)"
                                    variant="outlined"
                                    fullWidth
                                    placeholder="Opcional"
                                    value={productAttributes.code ?? ""}
                                    onChange={(event) =>
                                        productEditable.set(
                                            "code",
                                            event.target.value.trim() ? event.target.value.trim() : null
                                        )
                                    }
                                />

                                <TextField
                                    id="productDescription"
                                    label="Descrição / observações (ajuda na busca na venda)"
                                    variant="outlined"
                                    fullWidth
                                    multiline
                                    minRows={2}
                                    value={productAttributes.description ?? ""}
                                    onChange={(event) => productEditable.set("description", event.target.value)}
                                />

                                <TextField
                                    id="name"
                                    label={productAttributes.name ? "Nome do produto" : null}
                                    placeholder="Produto sem título"
                                    variant="outlined"
                                    fullWidth
                                    value={productAttributes.name || ''}
                                    onChange={(event) => productEditable.set('name', event.target.value)}
                                />
                                <Stack spacing={1}>
                                    {nameSuggestLoading ? (
                                        <Typography variant="caption" color="text.secondary">
                                            Gerando sugestões de nome…
                                        </Typography>
                                    ) : null}
                                    {nameSuggestions.length > 0 ? (
                                        <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center">
                                            <Typography variant="caption" color="text.secondary" sx={{ width: "100%" }}>
                                                Sugestões (toque para aplicar):
                                            </Typography>
                                            {nameSuggestions.map((s) => (
                                                <Button
                                                    key={s}
                                                    size="small"
                                                    variant="outlined"
                                                    onClick={() => productEditable.set("name", s)}
                                                >
                                                    {s}
                                                </Button>
                                            ))}
                                        </Stack>
                                    ) : null}
                                </Stack>

                                <MoneyInput
                                    value={productAttributes.price}
                                    onChange={changed => productEditable.set('price', changed.value)}
                                    title={"Valor do Produto"}
                                    textFieldProps={{
                                        fullWidth: true
                                    }}
                                />

                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={productAttributes.trackStock !== false}
                                            onChange={(e) =>
                                                productEditable.set("trackStock", e.target.checked)
                                            }
                                            color="primary"
                                        />
                                    }
                                    label="Controlar estoque deste produto"
                                    sx={{ alignItems: "center", ml: 0 }}
                                />
                                <Typography variant="body2" color="text.secondary" sx={{ mt: -0.5, mb: 0.5 }}>
                                    Desative para serviços ou itens sem contagem (vendas não baixam quantidade).
                                </Typography>

                                {productAttributes.trackStock !== false ? (
                                    <TextField
                                        id="stock"
                                        label="Quantidade em estoque"
                                        type="number"
                                        placeholder="0"
                                        variant="outlined"
                                        fullWidth
                                        inputProps={{ min: 0, step: 1 }}
                                        value={productAttributes.stock ?? ""}
                                        onChange={(event) => {
                                            const v = event.target.value;
                                            productEditable.set(
                                                "stock",
                                                v === "" ? 0 : parseInt(v, 10) || 0
                                            );
                                        }}
                                    />
                                ) : null}

                                <Stack spacing={1.25} sx={{ width: "100%" }}>
                                    <Stack direction="row" spacing={1} alignItems="flex-start">
                                        <Autocomplete
                                            sx={{ flex: 1, minWidth: 0 }}
                                            value={{
                                                title: categories?.find((category) => category.id === productAttributes.category)?.title,
                                                id: productAttributes.category
                                            }}
                                            onChange={(event, newValue) => {
                                                (event.target as any).blur();
                                                if (typeof newValue === 'string') {
                                                    const existingCategory = categories?.find(category => category.title === newValue)

                                                    if (existingCategory) {
                                                        productEditable.set('category', existingCategory);
                                                        return;
                                                    }
                                                }

                                                if (newValue && typeof newValue === 'object' && newValue.id !== "_new") {
                                                    productEditable.set('category', newValue);
                                                    return;
                                                }


                                                const newTitle = typeof newValue === "string" ? newValue : newValue?.inputValue;
                                                if (!newTitle) {
                                                    return;
                                                }

                                                const newCategory = new ProductCategory(v4(), newTitle, '');
                                                productCategoriesRepository.save(newCategory);
                                                productEditable.set('category', newCategory);
                                                setCategories((items) => items ? [...items, newCategory] : [newCategory]);
                                                return;
                                            }}
                                            filterOptions={(options, params) => {
                                                const filtered = createFilterOptions<ItemAutoComplete>()(options, params);

                                                const { inputValue } = params;
                                                const isExisting = options.some((option) => inputValue === option.title);
                                                if (inputValue !== '' && !isExisting) {
                                                    filtered.push({
                                                        id: '_new',
                                                        title: `Adicionar "${inputValue}"`,
                                                        inputValue
                                                    });
                                                }

                                                return filtered;
                                            }}
                                            selectOnFocus
                                            clearOnBlur
                                            handleHomeEndKeys
                                            options={categories?.map((category) => ({
                                                title: category.title,
                                                id: category.id
                                            } as ItemAutoComplete)) || []}
                                            getOptionLabel={(option) => {
                                                if (typeof option === 'string') {
                                                    return option;
                                                }

                                                return option.title || '';
                                            }}
                                            renderOption={(props, option) => <li {...props}>{option.title}</li>}
                                            freeSolo
                                            loading={categories === undefined}
                                            fullWidth
                                            renderInput={(params) => (
                                                <TextField {...params} label="Categoria" fullWidth />
                                            )}
                                        />
                                        <Tooltip title="Nova categoria">
                                            <IconButton
                                                color="primary"
                                                sx={{ mt: 0.5 }}
                                                onClick={() => {
                                                    setNewCategoryTitle("");
                                                    setNewCategoryDescription("");
                                                    setCategoryModalOpen(true);
                                                }}
                                                aria-label="Adicionar categoria"
                                            >
                                                <Add />
                                            </IconButton>
                                        </Tooltip>
                                    </Stack>

                                    <Box
                                        sx={{
                                            pl: 0.5,
                                            ml: 0.25,
                                            borderLeft: 2,
                                            borderColor:
                                                !categorySupplierLoading &&
                                                    categorySupplierSuggestion &&
                                                    categorySupplierSuggestion.category.mode !== "none" ?
                                                    "primary.light" :
                                                    "transparent",
                                            transition: "border-color 0.2s ease",
                                        }}
                                    >
                                        {categorySupplierLoading ? (
                                            <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 0.25 }}>
                                                <CircularProgress size={14} thickness={5} />
                                                <Typography variant="caption" color="text.secondary">
                                                    Sugerindo categoria com base no nome…
                                                </Typography>
                                            </Stack>
                                        ) : null}
                                        {!categorySupplierLoading &&
                                            categorySupplierSuggestion &&
                                            categorySupplierSuggestion.category.mode !== "none" ? (
                                            <Stack spacing={0.75} sx={{ pt: 0.25 }}>
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{ fontWeight: 600, display: "block" }}
                                                >
                                                    Categoria sugerida — toque para aplicar
                                                </Typography>
                                                <Stack direction="row" flexWrap="wrap" gap={1}>
                                                    {categorySupplierSuggestion.category.mode === "existing" &&
                                                        categorySupplierSuggestion.category.existingId ?
                                                        (() => {
                                                            const ct = categories?.find(
                                                                (c) =>
                                                                    c.id ===
                                                                    categorySupplierSuggestion.category.existingId
                                                            );
                                                            return ct ? (
                                                                <Chip
                                                                    size="small"
                                                                    label={ct.title}
                                                                    onClick={() => void applyAiCategoryChip()}
                                                                    variant="outlined"
                                                                    color="primary"
                                                                />
                                                            ) : null;
                                                        })() :
                                                        null}
                                                    {categorySupplierSuggestion.category.mode === "new" &&
                                                        categorySupplierSuggestion.category.newTitle?.trim() ?
                                                        <Tooltip title="Cria uma categoria nova no cadastro e associa ao produto">
                                                            <Chip
                                                                size="small"
                                                                label={`Nova: ${categorySupplierSuggestion.category.newTitle.trim()}`}
                                                                onClick={() => void applyAiCategoryChip()}
                                                                variant="outlined"
                                                                color="secondary"
                                                            />
                                                        </Tooltip> :
                                                        null}
                                                </Stack>
                                            </Stack>
                                        ) : null}
                                    </Box>
                                </Stack>

                                {(() => {
                                    const hasPrivatePermission = user?.hasPermission([Permission.READ_WRITE_PRODUCTS_PRIVATE]) ?? false;
                                    const resolvedSupplierCosts: Array<{ supplierId: string; supplier: ProductSupplier | undefined; purchasePrice: number }> =
                                        ((productAttributes.supplierCosts ?? []) as any[]).map((c: any) => ({
                                            supplierId: c.supplierId,
                                            supplier: suppliers?.find(s => s.id === c.supplierId),
                                            purchasePrice: c.purchasePrice || 0,
                                        }));

                                    const removeSupplierCost = (idx: number) => {
                                        const updated = resolvedSupplierCosts
                                            .filter((_, i) => i !== idx)
                                            .filter(c => c.supplier)
                                            .map(c => ({ supplier: c.supplier!, purchasePrice: c.purchasePrice }));
                                        productEditable.set("supplierCosts", updated);
                                    };

                                    const updateSupplierCostPrice = (idx: number, price: number) => {
                                        const updated = resolvedSupplierCosts
                                            .filter(c => c.supplier)
                                            .map((c, i) => ({ supplier: c.supplier!, purchasePrice: i === idx ? price : c.purchasePrice }));
                                        productEditable.set("supplierCosts", updated);
                                    };

                                    const addSupplierToProduct = async () => {
                                        if (!addSupplierEntry) return;
                                        let sup: ProductSupplier | null = null;
                                        if (addSupplierEntry.id === "_new" && addSupplierEntry.inputValue) {
                                            sup = new ProductSupplier(v4(), addSupplierEntry.inputValue.trim());
                                            await productSupplierRepository.save(sup);
                                            setSupplier(prev => prev ? [...prev, sup!] : [sup!]);
                                        } else {
                                            sup = suppliers?.find(s => s.id === addSupplierEntry.id) ?? null;
                                        }
                                        if (!sup) return;
                                        if (resolvedSupplierCosts.some(c => c.supplierId === sup!.id)) {
                                            Swal.fire({ title: "Fornecedor já adicionado", icon: "warning" });
                                            return;
                                        }
                                        const existing = resolvedSupplierCosts
                                            .filter(c => c.supplier)
                                            .map(c => ({ supplier: c.supplier!, purchasePrice: c.purchasePrice }));
                                        productEditable.set("supplierCosts", [...existing, { supplier: sup, purchasePrice: hasPrivatePermission ? addSupplierPrice : 0 }]);
                                        setAddSupplierEntry(null);
                                        setAddSupplierPrice(0);
                                    };

                                    return (
                                        <Stack spacing={1.25} sx={{ width: "100%" }}>
                                            <Stack direction="row" alignItems="center" spacing={0.75}>
                                                <Typography variant="subtitle2" fontWeight={600}>
                                                    {hasPrivatePermission ? "Fornecedores e preço de compra" : "Fornecedores"}
                                                </Typography>
                                                {!hasPrivatePermission && (
                                                    <Tooltip title="Preços de compra são dados privados. Solicite permissão ao administrador.">
                                                        <LockOutlined fontSize="small" sx={{ color: "text.disabled" }} />
                                                    </Tooltip>
                                                )}
                                            </Stack>

                                            {resolvedSupplierCosts.length > 0 && (
                                                <Stack spacing={1}>
                                                    {resolvedSupplierCosts.map((entry, idx) => (
                                                        <Stack key={entry.supplierId} direction="row" spacing={1} alignItems="center"
                                                            sx={{ p: 1.5, border: 1, borderColor: "divider", borderRadius: 2 }}>
                                                            <Typography flex={1} variant="body2" fontWeight={500}>
                                                                {entry.supplier?.title ?? "Fornecedor removido"}
                                                            </Typography>
                                                            {hasPrivatePermission && (
                                                                <MoneyInput
                                                                    value={entry.purchasePrice}
                                                                    onChange={(c) => updateSupplierCostPrice(idx, c.value)}
                                                                    title="Preço de compra"
                                                                    textFieldProps={{ size: "small" as any, sx: { width: 160 } }}
                                                                />
                                                            )}
                                                            <Tooltip title="Remover fornecedor">
                                                                <IconButton size="small" color="error" onClick={() => removeSupplierCost(idx)}>
                                                                    <DeleteOutline fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </Stack>
                                                    ))}
                                                </Stack>
                                            )}

                                            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "flex-start" }}>
                                                <Autocomplete
                                                    sx={{ flex: 1, minWidth: 0 }}
                                                    value={addSupplierEntry}
                                                    onChange={(_event, newValue) => {
                                                        if (typeof newValue === "string") {
                                                            const existing = suppliers?.find(s => s.title === newValue);
                                                            setAddSupplierEntry(existing ? { title: existing.title, id: existing.id } : { id: "_new", inputValue: newValue });
                                                        } else {
                                                            setAddSupplierEntry(newValue as ItemAutoComplete | null);
                                                        }
                                                    }}
                                                    filterOptions={(options, params) => {
                                                        const filtered = createFilterOptions<ItemAutoComplete>()(options, params);
                                                        const { inputValue } = params;
                                                        const isExisting = options.some(o => inputValue === o.title);
                                                        if (inputValue !== "" && !isExisting) {
                                                            filtered.push({ id: "_new", title: `Adicionar "${inputValue}"`, inputValue });
                                                        }
                                                        return filtered;
                                                    }}
                                                    selectOnFocus
                                                    clearOnBlur
                                                    handleHomeEndKeys
                                                    options={suppliers?.map(s => ({ title: s.title, id: s.id } as ItemAutoComplete)) ?? []}
                                                    getOptionLabel={(option) => typeof option === "string" ? option : (option.title ?? "")}
                                                    renderOption={(props, option) => <li {...props}>{option.title}</li>}
                                                    freeSolo
                                                    loading={suppliers === undefined}
                                                    renderInput={(params) => <TextField {...params} label="Adicionar fornecedor" fullWidth />}
                                                />
                                                {hasPrivatePermission && (
                                                    <MoneyInput
                                                        value={addSupplierPrice}
                                                        onChange={(c) => setAddSupplierPrice(c.value)}
                                                        title="Preço de compra"
                                                        textFieldProps={{ sx: { width: { xs: "100%", sm: 160 } } }}
                                                    />
                                                )}
                                                <Stack direction="row" spacing={0.5} sx={{ mt: { xs: 0, sm: 0.5 } }}>
                                                    <Button
                                                        variant="outlined"
                                                        disabled={!addSupplierEntry}
                                                        onClick={() => void addSupplierToProduct()}
                                                    >
                                                        Adicionar
                                                    </Button>
                                                    <Tooltip title="Novo fornecedor">
                                                        <IconButton
                                                            color="primary"
                                                            onClick={() => { setNewSupplierTitle(""); setSupplierModalOpen(true); }}
                                                            aria-label="Adicionar fornecedor"
                                                        >
                                                            <Add />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Stack>
                                            </Stack>

                                            <Box
                                                sx={{
                                                    pl: 0.5,
                                                    ml: 0.25,
                                                    borderLeft: 2,
                                                    borderColor:
                                                        !categorySupplierLoading &&
                                                            categorySupplierSuggestion &&
                                                            categorySupplierSuggestion.supplier.mode !== "none" ?
                                                            "primary.light" :
                                                            "transparent",
                                                    transition: "border-color 0.2s ease",
                                                }}
                                            >
                                                {categorySupplierLoading ? (
                                                    <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 0.25 }}>
                                                        <CircularProgress size={14} thickness={5} />
                                                        <Typography variant="caption" color="text.secondary">
                                                            Sugerindo fornecedor com base no nome…
                                                        </Typography>
                                                    </Stack>
                                                ) : null}
                                                {!categorySupplierLoading &&
                                                    categorySupplierSuggestion &&
                                                    categorySupplierSuggestion.supplier.mode !== "none" ? (
                                                    <Stack spacing={0.75} sx={{ pt: 0.25 }}>
                                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block" }}>
                                                            Fornecedor sugerido — toque para adicionar
                                                        </Typography>
                                                        <Stack direction="row" flexWrap="wrap" gap={1}>
                                                            {categorySupplierSuggestion.supplier.mode === "existing" &&
                                                                categorySupplierSuggestion.supplier.existingId ?
                                                                (() => {
                                                                    const sp = suppliers?.find(s => s.id === categorySupplierSuggestion.supplier.existingId);
                                                                    return sp ? (
                                                                        <Chip size="small" label={sp.title} onClick={() => void applyAiSupplierChip()} variant="outlined" color="primary" />
                                                                    ) : null;
                                                                })() : null}
                                                            {categorySupplierSuggestion.supplier.mode === "new" && categorySupplierSuggestion.supplier.newTitle?.trim() ?
                                                                <Tooltip title="Cria um fornecedor novo e associa ao produto">
                                                                    <Chip
                                                                        size="small"
                                                                        label={`Novo: ${categorySupplierSuggestion.supplier.newTitle.trim()}`}
                                                                        onClick={() => void applyAiSupplierChip()}
                                                                        variant="outlined"
                                                                        color="secondary"
                                                                    />
                                                                </Tooltip> : null}
                                                        </Stack>
                                                    </Stack>
                                                ) : null}
                                            </Box>
                                        </Stack>
                                    );
                                })()}

                                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ pt: 1 }}>
                                    <Button fullWidth variant="contained" color="primary" disabled={savingProduct} onClick={async () => {
                                        setSalvingProduct(true);
                                        try {
                                            if (!productId) {
                                                const allProducts = await productRepository.getAll("name");
                                                const limit = moduleAccess.limits.productsLimit;
                                                if (Number.isFinite(limit) && allProducts.length >= limit) {
                                                    await Swal.fire({
                                                        title: "Limite de produtos atingido",
                                                        text: "Seu plano atual não permite cadastrar mais produtos.",
                                                        icon: "warning",
                                                    });
                                                    return;
                                                }
                                            }
                                            if (!productEditable.validate()) {
                                                return;
                                            }
                                            const code = (productEditable.product.getCode() ?? "").trim();
                                            if (code.length > 0) {
                                                const taken = await productRepository.codeTakenByOther(code, productEditable.product.id);
                                                if (taken) {
                                                    await Swal.fire({
                                                        title: "Código em uso",
                                                        text: "Já existe outro produto com este código. Use um código diferente ou deixe em branco.",
                                                        icon: "error",
                                                    });
                                                    return;
                                                }
                                            }
                                            await productEditable.save();
                                            try {
                                                const snap = priceSnapshotRef.current;
                                                const newPrice = productEditable.product.getPrice();
                                                const newCosts = productEditable.product.getSupplierCosts();
                                                const isNew = !productId;
                                                const priceChanged = isNew || snap === null || newPrice !== snap.salePrice;
                                                const costsChanged = isNew || snap === null ||
                                                    JSON.stringify(newCosts.map(c => ({ id: c.supplier.id, p: c.purchasePrice }))) !==
                                                    JSON.stringify(snap.supplierCosts.map(c => ({ id: c.supplierId, p: c.purchasePrice })));
                                                if (priceChanged || costsChanged) {
                                                    const costEntries = newCosts.map(c => ({
                                                        supplierId: c.supplier.id,
                                                        supplierName: c.supplier.title ?? c.supplier.id,
                                                        purchasePrice: c.purchasePrice,
                                                    }));
                                                    await recordPriceHistory(productEditable.product.id, {
                                                        salePrice: newPrice,
                                                        supplierCosts: costEntries,
                                                        changedByUserId: user?.id ?? "",
                                                        source: "form",
                                                    });
                                                    priceSnapshotRef.current = { salePrice: newPrice, supplierCosts: costEntries };
                                                }
                                            } catch (err) {
                                                console.error("Price history recording failed", err);
                                            }
                                            analyticsService.trackEvent("product_saved", {
                                                mode: productId ? "edit" : "create",
                                                track_stock: productEditable.product.getTrackStock(),
                                                has_supplier: Boolean(productEditable.product.getSulier()),
                                                has_category: Boolean(productEditable.product.getCategory()),
                                                price: productEditable.product.getPrice(),
                                            });
                                            if (!productId) {
                                                symplouNovoProductDraft = null;
                                                navigate(`/produtos/editar/${productEditable.product.id}`, { replace: true });
                                            }
                                        } finally {
                                            setSalvingProduct(false);
                                        }
                                    }}>
                                        {savingProduct ? <CircularProgress size={24} color="inherit" /> : "Salvar alterações"}
                                    </Button>
                                    <Button fullWidth variant="outlined" color={productEditable.touched ? "error" : "primary"} onClick={() => {
                                        if (productEditable.touched) {
                                            Swal.fire({
                                                title: 'Tem certeza?',
                                                text: "Você perderá todas as alterações feitas neste produto!",
                                                icon: 'warning',
                                                showCancelButton: true,
                                                confirmButtonColor: '#d33',
                                                cancelButtonColor: '#3085d6',
                                                confirmButtonText: 'Sim, tenho certeza!',
                                                cancelButtonText: 'Continuar editando'
                                            }).then((result) => {
                                                if (result.isConfirmed) {
                                                    navigate('/produtos');
                                                }
                                            });

                                            return;
                                        }

                                        navigate('/produtos');
                                    }}>
                                        {productEditable.touched ? "Descartar" : "Voltar à lista"}
                                    </Button>
                                </Stack>
                            </Stack>
                        </Grid>
                    </Grid>
                </form>
            </Box>

            {mode === ProductFormMode.EDITING && productEditable && (
                <PriceHistoryChart productId={productEditable.product.id} />
            )}

            <Dialog
                open={enhanceImagePreview !== null}
                onClose={discardEnhanceImagePreview}
                maxWidth="md"
                fullWidth
                scroll="body"
            >
                <DialogTitle>Comparar fotos</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        À esquerda, a foto atual do produto. À direita, o resultado gerado pela IA. Só ao
                        confirmar a foto do produto será substituída no cadastro.
                    </Typography>
                    {enhanceImagePreview ? (
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                    Original
                                </Typography>
                                <Box
                                    component="img"
                                    src={enhanceImagePreview.originalUrl}
                                    alt=""
                                    sx={{
                                        width: "100%",
                                        maxHeight: 320,
                                        objectFit: "contain",
                                        borderRadius: 1,
                                        bgcolor: "grey.100",
                                        border: 1,
                                        borderColor: "divider",
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                    Gerada pela IA
                                </Typography>
                                <Box
                                    component="img"
                                    alt=""
                                    src={`data:${enhanceImagePreview.mimeType};base64,${enhanceImagePreview.imageBase64}`}
                                    sx={{
                                        width: "100%",
                                        maxHeight: 320,
                                        objectFit: "contain",
                                        borderRadius: 1,
                                        bgcolor: "grey.100",
                                        border: 1,
                                        borderColor: "divider",
                                    }}
                                />
                            </Grid>
                        </Grid>
                    ) : null}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1, flexWrap: "wrap" }}>
                    <Button
                        color="inherit"
                        onClick={discardEnhanceImagePreview}
                        disabled={imageUploading}
                    >
                        Descartar
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => void confirmEnhanceImageSave()}
                        disabled={imageUploading}
                    >
                        {imageUploading ? (
                            <CircularProgress size={22} color="inherit" />
                        ) : (
                            "Salvar nova foto"
                        )}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={categoryModalOpen} onClose={() => setCategoryModalOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Nova categoria</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        <TextField
                            label="Nome"
                            fullWidth
                            autoFocus
                            value={newCategoryTitle}
                            onChange={(e) => setNewCategoryTitle(e.target.value)}
                        />
                        <TextField
                            label="Descrição (opcional)"
                            fullWidth
                            multiline
                            minRows={2}
                            value={newCategoryDescription}
                            onChange={(e) => setNewCategoryDescription(e.target.value)}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCategoryModalOpen(false)}>Cancelar</Button>
                    <Button variant="contained" onClick={() => void saveNewCategoryFromModal()}>
                        Salvar e usar
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={supplierModalOpen} onClose={() => setSupplierModalOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Novo fornecedor</DialogTitle>
                <DialogContent>
                    <TextField
                        sx={{ mt: 1 }}
                        label="Nome"
                        fullWidth
                        autoFocus
                        value={newSupplierTitle}
                        onChange={(e) => setNewSupplierTitle(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSupplierModalOpen(false)}>Cancelar</Button>
                    <Button variant="contained" onClick={() => void saveNewSupplierFromModal()}>
                        Salvar e usar
                    </Button>
                </DialogActions>
            </Dialog>
        </Shell>
    );
}